import {
  createSlice,
  createAsyncThunk,
  createSelector } from
"@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import * as TYPES from "@src/redux/types";

const GRAPHQL = gql`
  query getClientOrganizationList(
    $filter: clientOrganizationFilterInput
    $pagination: paginationInput!
    $sort: [sortInput]
  ) {
    getClientOrganizationList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        rootLevelList {
          levelAttributeList {
            id
            name
            sortOrder
            isMandatory
            fieldType
            optionList
          }
          allowKitAssign
          allowOperatorLogin
          orderNumber
          parentLevelId
          name
          id
        }

        levelAttributeValueList {
          id
          levelAttributeId
          value
        }
        subjectLabel
        isActivated
        isParentArchived
        isArchived
        isSlaAccepted
        requireSlaAcceptance
        logoUrl
        mobile
        email
        type
        usernamePrefix
        name
        id
        groupId
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.OrganizationListState = {
  result: null,
  error: null,
  loading: false
};

export const getClientOrganizationListRequest = createAsyncThunk<
  TYPES.OrganizationList,
  {
    filter?: TYPES.ClientOrganizationFilterInput;
    pagination: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  "organization/get_client_organization_list",
  async (
  params: {
    filter?: TYPES.ClientOrganizationFilterInput;
    pagination: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getClientOrganizationList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || "An error occurred",
        statusCode: err.statusCode || "500"
      });
    }
  }
);

const getClientOrganizationListSlice = createSlice({
  name: "get_client_organization_list",
  initialState: initialState,
  reducers: {
    getClientOrganizationListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getClientOrganizationListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      getClientOrganizationListRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(
      getClientOrganizationListRequest.rejected,
      (state, action) => {
        state.error = action.payload as Error || {
          message: "Failed to fetch",
          statusCode: "500"
        };
        state.loading = false;
      }
    );
  }
});

export const { getClientOrganizationListClear } =
getClientOrganizationListSlice.actions;

export const getClientOrganizationList = createSelector(
  (state: RootState) => state.organization.getClientOrganizationList.result,
  (state: RootState) => state.organization.getClientOrganizationList.error,
  (state: RootState) => state.organization.getClientOrganizationList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getClientOrganizationListSlice.reducer;